import { ElectronService } from 'ngx-electron';
import { Injectable } from '@angular/core';
import { UsbDevice } from '../models/winco/usb-device.model';
import { EHardwareMode } from '../enums/hardware-mode.enum';
import { DialogBoxData, DialogBoxService, DialogBoxType } from 'dialog-box';
import { DllDevice, Info, InfoVersion } from 'info';
import { Usb } from 'usb';
import { Exit } from 'exit';
import { EventManagerService } from 'event-manager';
import { WincoEvents } from '../enums/winco-events..enum';

/**
 * Gestiona la comunicación con los componentes de Electron
 */
@Injectable({
  providedIn: 'root'
})
export class IpcService
{
  private _window:any; //instancia de window

  info: InfoVersion | undefined;
  drive:UsbDevice | undefined;

  constructor(private electronService: ElectronService,
              private dialogService: DialogBoxService,
              private eventManagerService:EventManagerService)
  {
    this._window = window;



    //si es una aplicación Electron añadimos los observables
    //de recepción de eventos
    if(this.electronService.isElectronApp)
    {
      this.eventManagerService.addEvent(WincoEvents.SAVE_FILE);
      this.eventManagerService.addEvent(WincoEvents.EXPORT_ANALYSIS_PDF);

      //reciben respuesta de una petición previa
      this._window.api.receive("save-file", (result) => this.eventManagerService.emitEvent(WincoEvents.SAVE_FILE, result));
      this._window.api.receive("export-analysis-pdf", (result) => this.eventManagerService.emitEvent(WincoEvents.EXPORT_ANALYSIS_PDF, result));

      //reciben eventos disparados directamente por electron
      this._window.api.receive('usb-added', (device:UsbDevice) => this.drive = device);
      this._window.api.receive('usb-removed', (device:UsbDevice) => this.drive = device);
    }
  }

  /**
   * Comprueba si existe el fichero de seguridad anti-copia del software.
   * Comprueba si existen las carpeta "data", "storage" y sus subcarpetas en la ubicación esperada.
   * Estas carpetas son necesarias para almacenar la base de datos, los vídeos e imágenes que utiliza la aplicación
   * @returns Promise<void>
   */
  initializeData(): Promise<void>
  {
    return new Promise((resolve, reject) =>
    {
      //enviamos orden al api para que compruebe si existe la carpeta
      //si no existe se creará
      // this._window.api.send("check-data");
      Info.check(DllDevice.WINCO_PRO).then(async result =>
      {
        this.info = result;

        //obtenemos los dispositivos USB conectados
        this.drive = await Usb.device();

        resolve();
      })
      .catch(err => reject(err));
    });
  }

  /**
   * Cierra la aplicación después de pedir una confirmación por parte del usuario
   */
  async quitApp()
  {
    if(this.info != null)
    {
      if(await Info.isDevelop() || await Usb.isMasterKey())
      {
        Exit.close();
      }
      else
      {
        if(this.info.hardwareMode === EHardwareMode.ALL_IN_ONE)
        {
          Exit.shutdown();
        }
        else
        {
          this.dialogService.show(new DialogBoxData(
            {
              type: DialogBoxType.CONFIRM,
              message: 'recuerda_apagar_equipo',
              acceptCallback: async () => Exit.shutdown()
            }));
        }
      }
    }
    else if(await Usb.isMasterKey())
    {
      Exit.close();
    }
    else
    {
      Exit.shutdown();
    }
  }

  /**
   * Guarda un fichero con los datos de log
   * @param file datos a escribir en el fichero
   * @returns Promise<string>
   */
  saveFile(file:string): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      try
      {
        if(this.electronService.isElectronApp)
        {
          const eventUuid = this.eventManagerService.subscribeEvent<any>(WincoEvents.SAVE_FILE, result =>
          {
            this.eventManagerService.unsubscribeEvent(eventUuid);

            //en función de la respuesta del api devolvemos un resultado
            if(result !== 'error' && result !== 'canceled')
              resolve(result);
            else
              reject(result);
          });

          //enviamos orden al api para que guarde los datos
          this._window.api.send("save-file", { file: file, drive: (this.drive) ? this.drive.path : 'C:\\' });
        }
        else
        {
          resolve('');
        }
      }
      catch
      {
        reject('error');
      }
    });
  }

  requestAnalysisExportPdf(uuid: string): Promise<string>
  {
    return new Promise((resolve, reject) =>
    {
      try
      {
        if(this.electronService.isElectronApp)
        {
          const eventUuid = this.eventManagerService.subscribeEvent<any>(WincoEvents.EXPORT_ANALYSIS_PDF, result =>
          {
            this.eventManagerService.unsubscribeEvent(eventUuid);

            if(typeof(result) === 'string' && result.toLowerCase().endsWith('.pdf'))
              resolve(result);
            else
              reject(typeof(result) === 'string' ? result : 'error');
          });

          this._window.api.send('export-analysis-pdf', { uuid });
        }
        else
        {
          resolve('');
        }
      }
      catch
      {
        reject('error');
      }
    });
  }

  notifyAnalysisExportReady()
  {
    if(this.electronService.isElectronApp)
    {
      this._window.api.send('analysis-export-ready', {});
    }
  }

  notifyAnalysisExportStatus(status: string)
  {
    if(this.electronService.isElectronApp)
    {
      this._window.api.send('analysis-export-status', { status });
    }
  }
}
